/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { EventEmitter, Injectable } from '@angular/core';
import { FileSizeFormatPipe, XsI18nPipe } from '@nx-fe/pipes';
import { WatcherService } from 'app/services/watcher.service';
import {
  ITicketConfiguration,
  AuthorizationService,
  ErrorHandlerService,
  FeatureToggleService,
  GlobalEventBusService,
  GroupContextService,
  GroupSettingsService,
  hd,
  ILicenceDetails,
  ITagConfiguration,
  LicenceStatus,
  LoggerService,
  PageTitleService,
  TicketContextService,
  TicketService,
  McConnectionsService,
  IMCConnection,
} from '@nx-fe/services';
import { Subscription, Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import {
  AttachmentService,
  IAttachmentModel,
  IAttachmentServiceConfig,
} from 'xsmodules/ticket/services/attachment.service';
import { filter, first, map } from 'rxjs/operators';
import {
  DeviceSnapshotSaveRequestStatus,
  IDeviceSnapshotStatus,
  SnapshotModel,
  SnapshotService,
} from 'xsmodules/ticket/services/snapshot.service';
import { WorkPanelManagerService, WorkPanelType } from 'xsmodules/remote-control/services/work-panel-manager.service';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { GlobalEvents } from 'app/core/global-event-constants';
import { RemoteControlService } from 'xsmodules/remote-control/services/remote-control.service';
import { DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';
import { IWorkPanelInformation } from 'xsmodules/ticket/components/work-panel/work-panel-rc2.models';
import {
  Activity,
  ActivityTypeEnum,
  DeviceCaptureActivity,
  EmailActivity,
  EmailRecipient,
  EmailRecipientWithUser,
  isAttachmentActivity,
  isDeviceCaptureActivity,
  isDeviceSnapshotActivity,
  isEmailRecipientWithUser,
  isNoteActivity,
  isOutboundEmailActivity,
  isRemoteControlActivity,
  NoteActivity,
  RemoteControlActivity,
  Ticket,
  TicketStatusEnum,
  WatcherDetails,
} from 'xsmodules/ticket/ticket.model';
import { SotiPopup } from '@soti/popup';
import {
  IOutboundEmailConfigV2,
  OutboundEmailSettingsService,
} from 'xsmodules/settings/components/email-settings/outbound-email-settings/outbound-email-settings.service';
import { DeviceLogRequestStatus } from 'xsmodules/remote-control/services/rc-logs.service';
import { isEqual } from 'lodash';
import { DashboardPageService } from 'xsmodules/dashboard/dashboard-page.service';

type ITicketModel = hd.ITicketModel;
type IDeviceModel = hd.IDeviceModel;
type IError = hd.IError;
type IActivityModel = hd.IActivityModel;
type IEmailRecipient = hd.IEmailRecipient;
type IRemoteControlActivity = hd.IRemoteControlActivity;
type IDropdownListItem = hd.IDropdownListItem;
type IEmailActivity = hd.IEmailActivity;
type INoteActivity = hd.INoteActivity;
type ITicketWorkInfoModel = hd.ITicketWorkInfoModel;
type IAttachmentActivity = hd.IAttachmentActivity;

export const enum DeviceCaptureActivityStatus {
  unknown = 0,
  initialized = 1,
  inProgress = 2,
  finished = 3,
  error = 4,
}

export const enum DeviceCaptureActivityTypes {
  none = 0,
  video = 1,
  image = 2,
  adbLogs = 4,
  file = 8,
  snapshot = 16,
  audiovideo = 32,
}

export type UnbindListenerFunction = () => void;

/** Action to take when attempting to update a ticket property */
export const enum UpdatePropertyAction {
  /** Set ticket property only after the update operation is successful */
  SetAfter,
  /** Set ticket property before the update operation and reset it to its original value on failure */
  SetFirstResetAfter,
}

export enum SidePanel_Status {
  collapsed = 'Collapsed',
  expanded = 'Expanded',
}

export interface IDeviceTicketData {
  device: IDeviceModel;
  ticketId: string;
}

/** Ticket controller implementation. */
@Injectable()
export class TicketPageService {
  /** Ticket Service Functions */
  /** @see ITicketService.getTicketStatus */
  getTicketStatus = this.ticketService.getTicketStatus;

  /** @see ITicketService.getTicketPriority */
  getTicketPriority = this.ticketService.getTicketPriority;

  /** @see ITicketService.getTicketReportedMethod */
  getTicketReportedMethod = this.ticketService.getTicketReportedMethod;

  descriptionState: string;

  isCollapsed = false;
  sidePanelEvent = new BehaviorSubject(SidePanel_Status.expanded);

  workPanelCollapsed = true;
  workPanelEvent = new BehaviorSubject(SidePanel_Status.collapsed);

  ticketConfigSubject = new ReplaySubject<ITicketConfiguration>(1);

  attachmentConfigSubject = new ReplaySubject<IAttachmentServiceConfig>(1);

  description: string;

  watchersList: WatcherDetails[];

  refreshedActivities: hd.IActivityModel[] = [];

  isSplashScreen = true;

  isRemoteControlStarted = false;

  rcDeviceInstanceName;

  /** If the Assist licence is expired */
  isLicenceActive = false;

  groupName: string;

  targetResolutionDate: string;

  elapsedTime: string;

  serviceTarget: string;

  status: string;

  showOtherDates = false;

  _isPreDefinedTagRequiredWindowToggleOpen = false;

  recipientsUserInfos: EmailRecipientWithUser[] = [];

  /** Licence Information */
  private licenceInfo: ILicenceDetails;

  // Ticket is initially set to loading
  private _isLoading = true;

  private _isProcessing = false;

  private _isValidationEnabled = false;

  private _transitionInProgress: { [key: string]: boolean } = {};

  private _minimizedPanels: { [key: string]: boolean } = {};

  private _rcPanelState = false;

  private _isRcMinimized = false;

  private _maxAttachmentSize = 0;

  /** Maximum upload file size of the Attachment APIs */
  private readonly _maxApiFileSize = 2000000000 / 1024 / 1024;

  private _processingProperties: { [property: string]: boolean } = {};

  private _isEditingDescription = false;

  private _isTicketDeleted = false;

  private _emailConfig: IOutboundEmailConfigV2;

  private _tagConfig: ITagConfiguration;

  private _isTicketUpdated = false;

  private _ticketConfiguration: ITicketConfiguration;

  private _pullTheVideoAttachmentRetryInterval = 3000;

  private _pullTheVideoAttachmentInitialDelay = 500;

  private _pullTheVideoAttachmentRetryCount = 20;

  private _pullDeviceLogResetInterval = 30000;

  private isRcPoppedOut = false;

  private _currentEmailActivityIndex: number;

  private _unbindHandlers: UnbindListenerFunction[] = [];

  private _devicesCheckinSubscription: Subscription;
  // We start with 3 active panels and on init we reduce it.
  private _activePanelsCount = 3;

  private _dueDate = { id: '2', captionId: 'ticket_Due_On_Caption', date: () => this.ticket.resolutionTargetDate };
  private _updateDate = { id: '1', captionId: 'ticket_Updated_On_Caption', date: () => this.ticket.updateDate };
  private _createdDate = { id: '0', captionId: 'ticket_Created_On_Caption', date: () => this.ticket.creationDate };

  // Flag to suppress Confirm Discard message when user tries to navigate away
  private _suppressDiscardConfirmation = false;

  private _previewSnapshotSub: Subscription;
  private _attachScreenshotSub: Subscription;
  private _openLiveSnapshotSub: Subscription;
  private _remoteControlMaximizedSub: Subscription;
  private _startRemoteControlSub: Subscription;

  private deviceSnapshotStatusChanged: BroadcastService<IDeviceSnapshotStatus> =
    new BroadcastService<IDeviceSnapshotStatus>(BroadcastChannels.onDeviceSnapshotStatusChanged);
  private readonly popoutRemoteControlStart: BroadcastService<IDeviceModel> = new BroadcastService<IDeviceModel>(
    BroadcastChannels.onPopoutRemoteControlStart
  );
  private readonly popoutRemoteControlStopped: BroadcastService<void> = new BroadcastService<void>(
    BroadcastChannels.onPopoutRemoteControlStopped
  );
  private readonly workPanelPopout: BroadcastService<boolean> = new BroadcastService<boolean>(
    BroadcastChannels.onWorkPanelPopout
  );
  ticket: Ticket;
  ticketSubmitTriggered: boolean;

  public watcherRefreshed = new BehaviorSubject(null);

  constructor(
    private $filter: XsI18nPipe,
    private ticketService: TicketService,
    public ticketContextService: TicketContextService,
    private pageTitleService: PageTitleService,
    private remoteControlService: RemoteControlService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private errorHandlerService: ErrorHandlerService,
    private attachmentService: AttachmentService,
    private authorizationService: AuthorizationService,
    private featureToggleService: FeatureToggleService,
    public $log: LoggerService,
    private mcConnectionService: McConnectionsService,
    private groupContextService: GroupContextService,
    private globalEventBusService: GlobalEventBusService,
    private snapshotService: SnapshotService,
    private workPanelManagerService: WorkPanelManagerService,
    private watcherService: WatcherService,
    private outboundEmailService: OutboundEmailSettingsService,
    private dashboardService: DashboardPageService,
    private groupSettingsService: GroupSettingsService,
    private fileSizeFormatter: FileSizeFormatPipe
  ) {}

  initialize = (ticket: Ticket) => {
    this.$log.info('ticket-page.service initializing', ticket.ticketId);

    // Unwrap activity details - it does not need to be an array
    // TODO: confirm if this applies to ALL activities
    ticket.activities = ticket.activities || [];
    ticket.activities.forEach(activity => {
      this.patchActivity(activity);
      console.log('activity', activity);
    });

    this.ticket = ticket;
    this.ticketSubmitTriggered = false;

    // Set the selected ticket on the ticket context service
    this.ticketContextService.ticket = (<unknown>ticket) as ITicketModel;
    this.groupContextService.currentGroupId = ticket.groupId;

    this.ticketContextService.licenceDetails.pipe(filter(details => details !== null)).subscribe(details => {
      this.licenceInfo = details;
      // Reset license status on ticketContextService
      if (this.licenceInfo.$daysRemaining > 0) {
        this.groupContextService
          .forGroup(this.ticket.groupId, () => this.outboundEmailService.getGroupEmailConfig(this.ticket.groupId))
          .subscribe(
            res => {
              this._emailConfig = res;
            },
            error => {
              this._emailConfig = undefined;
              this.$log.debug(error);
            }
          );
      }
      if (this.licenceInfo.status === LicenceStatus.active) {
        this.isLicenceActive = true;
      }
    });

    // Get groupname details for the ticket
    if (this.featureToggleService.isEnabled('Groups') && ticket.groupId) {
      this.groupName = this.groupContextService.getGroupName(ticket.groupId);
    }

    // Get group defined tag enable configuration
    this.groupSettingsService.getTagConfiguration().subscribe(tagConfig => {
      this.groupSettingsService.getTicketConfiguration().subscribe({
        next: (ticketConfiguration: ITicketConfiguration) => {
          this._ticketConfiguration = ticketConfiguration;
          this.ticketConfigSubject.next(ticketConfiguration);
        },
        error: error => {
          this.$log.debug(error);
        },
      });
      this._tagConfig = tagConfig;
    });

    this.remoteControlService.clearNewDeviceCaptureActivities();
    this.remoteControlService.setNewDeviceCaptureActivityCallback(this.loadNewDeviceCaptureActivities);
    this.remoteControlService.setAddNewDeviceCaptureActivityCallback(
      this.remoteControlService.addNewDeviceCaptureActivity
    );

    // Initialize the controller for the ticket
    this.initExistingTicket(ticket);

    // Lookup max attachment size
    // XTODO:
    this.attachmentService.getConfig().subscribe((result: IAttachmentServiceConfig) => {
      this._maxAttachmentSize = result.maxFileSize;
      this.attachmentConfigSubject.next(result);
    });

    if (this.featureToggleService.isEnabled('AdvancedSla')) {
      this.elapsedTime = null;
      this.serviceTarget = null;
      this.status = null;
      this.configureSlaTimer();
    }

    // Resize to execute a digest for hiding the splash screen.  Does not need to run a digest when RC is visible.
    // XTODO: needed?
    // $(window).on('resize', () => {
    //     if (!this.remoteControlService.anyRcVisible()) {
    //         this.$rootScope.$apply();
    //     }
    // });

    // To trigger a window blur event on Iframe focus
    // This is the way to let know the parent window that Iframe is clicked
    // $(window).on('blur', () => {
    //     // To close the dropdown (priority, status etc) we have to click anywhere in the window
    //     // .inbound-email-caption will be there above iFrame and trigger a click event
    //     $('.inbound-email-caption').trigger('click');
    // });

    // Listen for RC popout events
    // XTODO:
    this.deviceStateRegistry.addRemoteControlPopoutCallback(this.onRemoteControlPopout);
    this.workPanelPopout.onMessage = (poppedOut: boolean) => {
      this.onRemoteControlPopout(poppedOut);
    };

    this._startRemoteControlSub = this.globalEventBusService.register(
      GlobalEvents.startRemoteControl,
      (device: IDeviceModel) => {
        this.startRemoteControl(device);
      }
    );

    this._openLiveSnapshotSub = this.globalEventBusService.register(GlobalEvents.openLiveSnapshot, () => {
      this.rcOrSnapshotStarted();
    });

    // close association panel while expanding RC or live snapshot panel [HD-12831]
    this._remoteControlMaximizedSub = this.globalEventBusService.register(GlobalEvents.remoteControlMaximized, () => {
      this.closeAssoicationPanel();
    });

    this._attachScreenshotSub = this.globalEventBusService.register(GlobalEvents.attachScreenshot, (result: any) => {
      this.attachScreenshot(result);
      //     if (this.$scope.$$phase || this.$scope.$root.$$phase) {
      //         this.$timeout();
      //     } else {
      //         this.$scope.$apply();
      //     }
    });

    // const unbindRequestScrollToDraftEmail = this.$scope.$on('requestScrollToDraftEmail', (event: ng.IAngularEvent) => {
    //     this.scrollToDraftEmail();
    // });
    // this._unbindHandlers.push(unbindRequestScrollToDraftEmail);

    if (this.deviceSnapshotStatusChanged.isClosed) {
      this.deviceSnapshotStatusChanged = new BroadcastService<IDeviceSnapshotStatus>(
        BroadcastChannels.onDeviceSnapshotStatusChanged
      );
    }
    this.deviceSnapshotStatusChanged.onMessage = this.onDeviceSnapshotStatusChanged;
    this.popoutRemoteControlStart.onMessage = this.onPopoutRemoteControlStart;
    this._isValidationEnabled = false;
  };

  // relies on component lifecycle event
  destroy = () => {
    this.groupContextService.currentGroupId = undefined;
    this.recipientsUserInfos = [];
    // Unregister callbacks on destroy
    this.deviceStateRegistry.removeRemoteControlPopoutCallback(this.onRemoteControlPopout);

    if (this.workPanelPopout) {
      this.workPanelPopout.close();
    }

    // End the current rc session
    // in case there is a previous open
    if (this.deviceStateRegistry.anyRcConnected()) {
      let deviceState = this.deviceStateRegistry.getCurrentState();
      if (deviceState && deviceState.device) {
        if (this.remoteControlService.popoutWindow) {
          this.remoteControlService.popoutWindow.close(); //close rc pop out window
        }
        this.remoteControlService.stopRemoteControl(deviceState.device, true);
      }
    }

    // // Unbind event handlers (FIFO)
    // while (this._unbindHandlers.length > 0) {
    //    (this._unbindHandlers.shift())(); // Call the unbind function
    // }

    // // Unsubscribe subscriptions
    // if (this._devicesCheckinSubscription) {
    //     this._devicesCheckinSubscription.unsubscribe();
    // }

    // if (this._previewSnapshotSub) {
    //     this._previewSnapshotSub.unsubscribe();
    // }

    if (this._attachScreenshotSub) {
      this._attachScreenshotSub.unsubscribe();
    }

    if (this._openLiveSnapshotSub) {
      this._openLiveSnapshotSub.unsubscribe();
    }

    if (this._remoteControlMaximizedSub) {
      this._remoteControlMaximizedSub.unsubscribe();
    }

    if (this._startRemoteControlSub) {
      this._startRemoteControlSub.unsubscribe();
    }

    // Close broadcast channels
    if (this.deviceSnapshotStatusChanged) {
      this.deviceSnapshotStatusChanged.close();
    }

    if (this.popoutRemoteControlStart) {
      this.popoutRemoteControlStart.close();
    }

    this.resetPanelsState();
    //this.sabhaClientService.removeSabhaMinifiedView();
    this.ticket = null;
  };

  get showUpdateDate(): boolean {
    if (this.featureToggleService.isEnabled('AdvancedSla')) {
      return !this.showSlaTimer;
    }
    return true;
  }

  get showSlaTimer(): boolean {
    return this.isOpenOrPending && this.ticketContextService.canViewSLA();
  }

  get reporterPublicId(): string {
    return this.ticket.reporterId;
  }

  get sidePanelStatus(): Observable<SidePanel_Status> {
    return this.sidePanelEvent.asObservable();
  }

  get isAssociationMinimized(): boolean {
    return this.isCollapsed;
  }

  sidePanelCollapse(isCollapsed: boolean): void {
    if (this.isCollapsed !== isCollapsed) {
      this.isCollapsed = isCollapsed;
      if (isCollapsed) {
        this.sidePanelEvent.next(SidePanel_Status.collapsed);
      } else {
        this.sidePanelEvent.next(SidePanel_Status.expanded);
      }
    }
  }

  get workPanelStatus(): Observable<SidePanel_Status> {
    return this.workPanelEvent.asObservable();
  }

  get isRCMinimized(): boolean {
    return this.workPanelCollapsed;
  }

  workPanelCollapse(isCollapsed: boolean): void {
    if (this.workPanelCollapsed !== isCollapsed) {
      this.workPanelCollapsed = isCollapsed;
      if (isCollapsed) {
        this.workPanelEvent.next(SidePanel_Status.collapsed);
      } else {
        this.workPanelEvent.next(SidePanel_Status.expanded);
      }
    }
  }

  // Called in main window context, to start a remote control session.
  // If startPoppedOut is true, then there is a popout waiting for the remote control session,
  // This means it should NOT be rendered in main window
  startRemoteControl = (device: IDeviceModel, startPoppedOut: boolean = false) => {
    // Clear all failed RC sessions
    if (this.deviceStateRegistry.canRemoteControl(device) && !this.deviceStateRegistry.isRemoteControlActive(device)) {
      // remove a failed RC activity (e.g. with undefined start date) if any
      if (this.hasActivities()) {
        for (let i = 0; i < this.ticketContextService.activities.length; i += 1) {
          const activity = this.ticketContextService.activities[i];
          if (activity.activityTypeId === ActivityTypeEnum.remoteControl) {
            const rcActivity = <RemoteControlActivity>activity;
            // If the RC activity has an error set, remove it before starting the new RC session
            if (rcActivity.$$error) {
              const deviceState = this.deviceStateRegistry.getState(rcActivity.details.deviceId);
              if (deviceState) {
                this.remoteControlService.stopRemoteControl(deviceState.device);
              } else {
                this.$log.debug('Failed to lookup device state for erroneous rc activity');
              }

              this.ticketContextService.activities.splice(i, 1);
              this.confirmAndStartRemoteControl(device, startPoppedOut);
              return;
            }
          }
        }
      }
      this.confirmAndStartRemoteControl(device, startPoppedOut);
    } else {
      if (!device.isAgentOnline) {
        const error: IError = {
          error_description: this.$filter.transform(
            'ticket_Failed_RemoteControl_Offline',
            device.isRemoteViewOnly
              ? this.$filter.transform('sdd_RemoteView')
              : this.$filter.transform('sdd_RemoteControl')
          ),
        };
        this.errorHandlerService.setError(error);
      }
    }
  };

  /** Sets the status based on the specified status id */
  setStatus(status: string) {
    let statusValue: number;
    switch (status) {
      case 'draft': // This shouldn't happen?
        statusValue = TicketStatusEnum.draft;
        break;
      case 'open':
        statusValue = TicketStatusEnum.open;
        break;
      case 'pending':
        statusValue = TicketStatusEnum.pending;
        break;
      case 'resolved':
        statusValue = TicketStatusEnum.resolved;
        break;
      default:
        return;
    }

    if (statusValue === TicketStatusEnum.resolved && this.isNoteRequiredOnResolve() && this.resolutionNoteRequired()) {
      this.globalEventBusService.broadcast(GlobalEvents.resolutionNoteRequired, null);
      return;
    }

    if (statusValue === TicketStatusEnum.resolved && this.validateAndShowPredefinedTagMessage()) {
      // Do nothing when the Pre-Defined Tag Requirements was not accomplished
      return;
    } else {
      this.updateProperty('status', status).then((ticket: Ticket) => {
        this.ticket.submissionDate = ticket.submissionDate;
        this.ticket.resolutionTargetDate = ticket.resolutionTargetDate;
        if (this.featureToggleService.isEnabled('AdvancedSla')) {
          this.configureSlaTimer();
        }
      });
    }
  }

  public resetPanelsState(): void {
    this.sidePanelCollapse(false);
    this.workPanelCollapse(true);
  }

  private isResolutionNote = (activity: Activity): boolean =>
    activity && isNoteActivity(activity) && activity.details.isResolution;

  private resolutionNoteRequired = (): boolean => {
    let needToAddResolutionNote = false;
    // TODO: remove forced type
    ((<unknown>this.ticketContextService.activities) as Activity[]).forEach(activity => {
      if (this.isResolutionNote(activity) === true) {
        const activityNote = activity;
        if (!activityNote.description || activityNote.description.trim().length === 0) {
          this.focusOnActivity(activityNote);
          needToAddResolutionNote = true;
        }
      }
    });
    return needToAddResolutionNote;
  };

  private focusOnActivity = (activity: Activity) => {
    // TODO: Focus on activity
    // const element = this.$element.find(`#${activity.$repeaterId}`);
    // angular.element(element).focus();
    // this.$scope.$root.$broadcast('scrollFromBottom', this.GetActivityIndexFromBottom(activity));
  };

  private GetActivityIndexFromBottom = (activity: hd.IActivityModel): number => {
    const activityPosition = this.ticketContextService.activities.findIndex(
      a => a.$repeaterId === activity.$repeaterId
    );
    return this.ticketContextService.activities.length - activityPosition - 1;
  };

  /** Sets the priority based on the specified priority id */
  setPriority = (value): void => {
    if (!this.isPriorityDisabled()) {
      this.updateProperty('priority', value).then((ticket: Ticket) => {
        this.ticket.submissionDate = ticket.submissionDate;
        this.ticket.resolutionTargetDate = ticket.resolutionTargetDate;
        if (this.featureToggleService.isEnabled('AdvancedSla')) {
          this.configureSlaTimer();
        }
      });
    }
  };

  /** Sets the reported method based on the specified method id */
  setReportedMethod = (value): void => {
    this.updateProperty('reportedMethod', value);
  };

  /** Sets the title to the given value */
  setTitle = value => {
    this.updateProperty('title', value, UpdatePropertyAction.SetAfter, '', 'subject');
  };

  /** Sets the description to the given value */
  setDescription = (value): ng.IPromise<Ticket> =>
    this.updateProperty('description', value, UpdatePropertyAction.SetAfter).then(result => {
      this.updateDescriptionState();
      return result;
    });

  public isAllFieldsPresent = (): boolean => !(this.isInvalidReporter() || this.isInvalidTitle());

  private isInvalidReporter = () => this.ticket.reporterId === undefined || this.ticket.reporterId === null;
  private isInvalidTitle = () => !(this.ticket.title && this.ticket.title.length > 0);

  /** Submits a ticket as the specified status */
  submitAs = (status: string): void => {
    this.ticketSubmitTriggered = true;
    if (this.isProcessing()) {
      // Do nothing when the ticket is already opened
      return;
    }

    if (!this.isNewTicket()) {
      this.setStatus(status);
      return;
    }

    // Enable form validation
    this._isValidationEnabled = true;
    // Re-enable Confirm Discard message
    this._suppressDiscardConfirmation = false;

    if (this.isInvalidReporter() && this.isAssociationMinimized) {
      // XTODO:  fire event from this service and handle following in component
      this.sidePanelCollapse(true);
      return;
    }

    // If all required fields are present, submit the ticket
    if (this.isAllFieldsPresent()) {
      if (status === 'resolved' && this.validateAndShowPredefinedTagMessage()) {
        // Do nothing when the Pre-Defined Tag Requirements was not accomplished
        return;
      }

      this.submitAsInternal(status);
    }
  };

  private submitAsInternal(status: string) {
    if (this.featureToggleService.isEnabled('ResolutionNotes')) {
      if (status === 'resolved' && this.isNoteRequiredOnResolve() && this.resolutionNoteRequired()) {
        // XTODO: globalEventService
        this.globalEventBusService.broadcast(GlobalEvents.resolutionNoteRequired, null);
      } else {
        this._isProcessing = true;
        this.ticketService.submitTicket(this.ticket.ticketId, status).subscribe(result => {
          // Forward to ticket list page when status update is complete
          if (result !== 0) {
            this.goToDashboard();
            this._isValidationEnabled = false;
            this.ticket.status = status;
          }
          this._isProcessing = false;
        });
      }
    } else {
      if (this.isNoteRequiredOnResolve() && status === 'resolved') {
        /** Show Mandatory Note window and update status with entered note */
        // XTODO: modal
        // const modalInstance = this.$uibModal.open({
        //     template: require('raw-loader!./mandatoryNote/mandatoryNote.tpl.html'),
        //     controller: 'saMandatoryNoteController',
        //     controllerAs: '$ctrl',
        //     scope: this.$scope,
        //     backdrop: true,
        //     size: 'sm'
        // });
        // modalInstance.result.then((noteText: string) => {
        //     this._isProcessing = true;
        //     this.updateProperty('status', status, UpdatePropertyAction.SetAfter, noteText).then(() => {
        //         // Forward to ticket list page when status update is complete
        //         this.back();
        //     }).finally(() => {
        //         this._isProcessing = false;
        //         this._isValidationEnabled = false;
        //     });
        // });
      } else {
        this._isProcessing = true;
        this.ticketService.submitTicket(this.ticket.ticketId, status).subscribe(result => {
          // Forward to ticket list page when status update is complete
          if (result !== 0) {
            this.goToDashboard();
            this._isValidationEnabled = false;
          }
          this._isProcessing = false;
        });
      }
    }
  }

  canDeleteTicket = () => this.ticketContextService.canDeleteTicket() && !this.isLoading();

  /** Delete the ticket */
  deleteTicket(goBack: boolean = false) {
    const propertyName = 'delete';
    // Don't allow two simultaneous requests for delete
    if (this.isProcessing(propertyName)) {
      const error: IError = {
        error_description: this.$filter.transform('ticket_Delete_AlreadyInProgress_Error'),
      };
      this.setError(error);
    }
    // Stop remote control section before ticket delete
    this.stopCurrentRemoteControlActivity();

    // Send the delete request to the server
    this.setProcessing(true, propertyName);
    this.ticketService.deleteTicket(this.ticket.ticketId.toString()).subscribe(
      next => {
        if (goBack) {
          this._isTicketDeleted = true;
          this.goToDashboard();
        }
      },
      error => {
        this.$log.debug(this.$filter.transform('ticket_Delete_Failed_Error') + this.ticket.ticketId);
        this.$log.error(error.toString());
        this.setError(error);
        this.setProcessing(false, propertyName);
      }
    );
  }

  /**
   * Updates a property on the ticket.
   *
   * The corresponding ticket property is updated locally immediately.
   * If the update fails (an error is returned from the server), then the ticket
   * property is reverted and the returned promise is rejected with an error.
   *
   * @param propertyName Ticket property name to be updated.
   * @param value New value for the property.
   * @param updateAction Action to be taken on the specified ticket property before and after update
   * (by default the property is set immediately and reset on failure).
   * @param noteText sent to backend unsure of purpose
   * @param updateProperty if db update referes to this by a name different to the property name, specify that here
   * @return A promise that is resolved when the update succeeds, or rejected if the update fails.
   */
  updateProperty = (
    propertyName: string,
    value: any,
    updateAction: UpdatePropertyAction = UpdatePropertyAction.SetFirstResetAfter,
    noteText: string = '',
    updateProperty?: string
  ): Promise<Ticket> =>
    // const deferred = this.$q.defer<ITicketModel>();
    new Promise<Ticket>((resolve, reject) => {
      // If the value has not changed (new value is the same as the existing value), do nothing and return success
      if (this.compareTicketProperties(propertyName, value)) {
        resolve(this.ticket);
        return;
      }

      // Don't allow two simultaneous requests on the same property
      if (this.isProcessing(propertyName)) {
        const error: IError = {
          error_description: this.$filter.transform('ticket_Property_Already_Updating_Error'),
        };
        reject(error);
        return;
      }

      // Save the current value and set the new value
      const originalValue = this.ticket[propertyName];
      if (updateAction === UpdatePropertyAction.SetFirstResetAfter) {
        this.ticket[propertyName] = value;
      }

      // Send the update to the server
      this.setProcessing(true, propertyName);

      this.ticketService
        .updateTicket(this.ticket.ticketId, [
          {
            property: updateProperty || propertyName,
            value,
            note: noteText,
          },
        ])
        .subscribe(
          updatedTicket => {
            this.ticketService
              .getTicket(this.ticket.ticketId.toString())
              .pipe(map(ticket => (<any>ticket) as Ticket))
              .subscribe(ticket => {
                // On success, update the ticket last updated time
                this.ticket.updateDate = ticket.updateDate;
                this.setProcessing(false, propertyName);
                this.clearError();
                if (updateAction === UpdatePropertyAction.SetAfter) {
                  this.ticket[propertyName] = value;

                  // if (propertyName === 'reporterPublicId' || propertyName === 'assigneePublicId') {
                  //   this.$rootScope.$emit('updateWatchers');
                  // }
                }
                resolve(ticket);
              });
          },
          (error: IError) => {
            // On error, revert the value to its original state
            this.$log.debug('Failed to update ticket property: ' + propertyName);
            this.$log.error(error);
            this.setError(error);
            if (updateAction === UpdatePropertyAction.SetFirstResetAfter) {
              this.ticket[propertyName] = originalValue;
            }
            this.setProcessing(false, propertyName);
            reject(error);
          }
        );
    });

  onDeviceAssociationLoaded = () => {
    this._isTicketUpdated = true;
  };

  /** Refreshes the ticket from the server */
  refresh = () => {
    /* Note this should eventually call...
     *   this.ticket.$get();
     * ...which will update this.ticket with all changes made on the server and cause all bindings to update.
     * However that is not the current desired behavior.
     */

    this.ticketService.getTicket(this.ticket.ticketId.toString()).subscribe(ticket => {
      this.ticket.updateDate = ticket.updateDate;
      this.ticket.watchers = ticket['watchers'];
      this.watcherRefreshed.next([]);
      this.ticket.devices = ticket['devices'];
      this.ticket.tags = ticket['tags'];
    });
  };

  private refreshActivities(ticket: Ticket) {
    const activities: Activity[] = ticket.activities || [];
    // TODO: Do we still need this? We don't have separate headers, so it would move if we need it
    // const headers = response.headers;
    // // this.ticketContextService.activities = activities;
    // if (headers.get('sa-allowable-actions')) {
    //   const acts = headers.get('sa-allowable-actions').split(',');
    //   this.ticketContextService.allowableActions = this.ticketContextService.allowableActions.concat(acts);
    //   this.ticket.$$allowableActions = this.ticketContextService.allowableActions.concat(acts);
    // }

    const filtered = activities.filter(x => {
      // displaying only successfully processed devicecapture activities
      // if (x.activityTypeId === ActivityTypeEnum.deviceSnapshot) {
      //   // Is this 'devicecapture'?
      //   return (x as DeviceCaptureActivity).status === DeviceCaptureActivityStatus.finished;
      // }
      if (this.isResolutionNote(x) && !this.ticketContextService.canAddResolutionNote()) {
        const activityNote = x as NoteActivity;
        return activityNote.description && activityNote.description.trim().length >= 0;
      }
      return true;
    });
    // TODO: Remove forced type once types are corrected
    this.ticketContextService.activities = (<unknown>filtered) as Activity[];

    // Add a resolution note placeholder if one does not exist and user has permissions
    // TODO: Remove forced type once types are corrected
    if (
      this.featureToggleService.isEnabled('ResolutionNotes') &&
      !this.ticketContextService.activities.some(x => this.isResolutionNote((<unknown>x) as Activity)) &&
      this.ticketContextService.canAddResolutionNote()
    ) {
      const activityTemplate = {
        isPrivate: false,
        isResolution: true,
      };
      this.addActivity(ActivityTypeEnum.note, true, activityTemplate);
    }
    this.setLoaded();
  }

  loadNewDeviceCaptureActivities = (isDraft: boolean = false, activityId: number = null) => {
    let retryCount = 0;
    const pullFunc = async (draft: boolean = false, id: number = null) => {
      const newDeviceCaptureActivities = this.remoteControlService.getNewDeviceCaptureActivities();
      if (newDeviceCaptureActivities && newDeviceCaptureActivities.length > 0) {
        const activityIds = newDeviceCaptureActivities.map(a => a.activityId);
        if (activityIds.length > 0) {
          this.ticketService.getActivities(parseInt(this.ticketContextService.ticket.ticketId, 10)).subscribe(
            (allActivities: any) => {
              const dcActivities: DeviceCaptureActivity[] = (allActivities as Activity[]).filter(a =>
                isDeviceCaptureActivity(a)
              );
              // console.log(allActivities);
              for (const activity of dcActivities) {
                this.patchActivity(activity);

                const existing = (<unknown>(
                  this.ticketContextService.activities.find(a => a.activityId === activity.activityId)
                )) as Activity;
                if (existing) {
                  Object.assign(existing, activity);
                } else if (activityIds.includes(activity.activityId)) {
                  // add a new activity
                  // console.log('[loadNewDeviceCaptureActivities] ADDING NEW activity ', activity.activityId);
                  this.ticketContextService.activities.push((<unknown>activity) as Activity);
                }
                // remove the activity from the queue of device capture activities that need
                // to be updated unless the activity is a draft device log. For draft device logs, one
                // more activity update is expect once all individual log files are successfully
                // uploaded.
                let videoNotReady: boolean | null = null;
                if (!draft || activity.activityId !== id) {
                  if (
                    isDeviceCaptureActivity(existing) &&
                    existing.details.captureType === DeviceCaptureActivityTypes.adbLogs
                  ) {
                    existing.details.$$isDraft = false;
                  } else if (
                    activity.details.captureType === DeviceCaptureActivityTypes.video ||
                    activity.details.captureType === DeviceCaptureActivityTypes.audiovideo
                  ) {
                    if (!activity.details.files?.length) {
                      videoNotReady = true;
                    } else if (
                      this.remoteControlService
                        .getNewDeviceCaptureActivities()
                        .some(x => x.activityId === activity.activityId)
                    ) {
                      videoNotReady = false;
                    }
                  }
                }
                // non-draft activity, remove it from the queue
                if (!draft) {
                  if (!videoNotReady) {
                    // wait for video activities that are yet to receive the file
                    this.remoteControlService.removeNewDeviceCaptureActivity(
                      (<unknown>activity) as hd.IDeviceCaptureActivity
                    );
                  }
                } else {
                  // only for draft activities, which should only ever be a new activity, deviceLog:
                  // Set up a long timeout to clean up that draft activity from the queue should that
                  // second update never arrive
                  // console.log('[loadNewDeviceCaptureActivities] DRAFT pending activity ', activity.activityId);
                  if (activity.details) {
                    activity.details.$$isDraft = true;
                  }
                  if (retryCount++ < this._pullTheVideoAttachmentRetryCount) {
                    setTimeout(() => pullFunc(false, id), this._pullDeviceLogResetInterval);
                  }
                }
                if (
                  activity.details.captureType === DeviceCaptureActivityTypes.video ||
                  activity.details.captureType === DeviceCaptureActivityTypes.audiovideo
                ) {
                  // check if there are any outstanding video activities. Do not
                  // change the state of teh recording, unless the video activity
                  // has just been completed
                  if (videoNotReady === false) {
                    this.remoteControlService.notifyNewVideoAvailable();
                  }
                } else if (activity.details.captureType === DeviceCaptureActivityTypes.adbLogs) {
                  // We finished the cycle of the device log request
                  this.remoteControlService.setDeviceLogRequestStatus(DeviceLogRequestStatus.None);
                }
                console.log('Newly captured ' + activity.details.captureType + ' activity attached successfully');
              }
              if (this.remoteControlService.getNewDeviceCaptureActivities().length === 0) {
                return;
              }
            },
            err => {
              this.$log.debug('Failed to load ticket activities');
              this.$log.error(err);
              this.setError(err);
            }
          );
        }
      }
      if (
        !draft &&
        this.remoteControlService.getNewDeviceCaptureActivities().length > 0 &&
        retryCount++ < this._pullTheVideoAttachmentRetryCount
      ) {
        setTimeout(() => pullFunc(draft, id), this._pullTheVideoAttachmentRetryInterval);
      }
    };
    if (this.remoteControlService.getNewDeviceCaptureActivities().length > 0) {
      setTimeout(() => pullFunc(isDraft, activityId));
      this._pullTheVideoAttachmentInitialDelay;
    }
  };

  /** Returns true if the ticket is new, false if it is an existing ticket. */
  isNewTicket(): boolean {
    return this.ticket?.status === 'draft';
  }

  /** Determines if the ticket and related information is currently being loaded */
  isLoading = (): boolean => this._isLoading;

  /**
   * Returns the endOfTransition that the current valid for the current browser
   */
  private getValidTransitionEndEventName(): string {
    const el = document.createElement('fakeelement');

    const transitions = {
      transition: 'transitionend',
      OTransition: 'oTransitionEnd',
      MozTransition: 'transitionend',
      WebkitTransition: 'webkitTransitionEnd',
    };

    let t: string;
    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }

  /**
   * Determines if an update is currently processing.
   *
   * @param propertyName Optional ticket property name to query the processing state of.
   */
  isProcessing = (propertyName?: string): boolean =>
    (propertyName === undefined && this._isProcessing) ||
    (propertyName !== undefined && this._processingProperties[propertyName] === true);

  isTransitionInProgress = (elementId: string): boolean => this._transitionInProgress[elementId];

  /**
   * Determines if validation is enabled.
   *
   * If an element is specified, the dirty and touched states of the element are also taken into consideration.
   *
   * @param element Optional element to consider.
   * @return True if validation is enabled, false otherwise.
   */
  isValidationEnabled = (): boolean => this._isValidationEnabled;

  /** Handler for the on-associate-reporter event - associates the given reporter ID to the ticket */
  associateReporter = (reportedId: string): Promise<Ticket> => {
    if (this.ticket) {
      const promise = this.updateProperty('reporterId', reportedId, UpdatePropertyAction.SetAfter);
      promise.then(() => {
        this.watcherService.AddWatcher(this.ticket.ticketId, reportedId).subscribe(data => {
          this.refresh();
        });
      });

      return promise;
    }
    this.$log.warn(this.$filter.transform('ticket_AssociateReporter_TicketDoesNotExist_Message'));
    return Promise.reject({ error_description: this.$filter.transform('ticket_DoesNotExist_Message') });
  };

  /** Handler for the on-disassociate-reporter event - disassociates the reporter by setting reporterPublicId to -1 */
  disassociateReporter = (reportedId: string): Promise<Ticket> => {
    if (this.ticket) {
      const promise = this.updateProperty('reporterId', null, UpdatePropertyAction.SetFirstResetAfter);
      promise.then(e => {
        this.watcherService.RemoveWatcher(this.ticket.ticketId, reportedId).subscribe(data => {
          this.refresh();
        });
      });
      return promise;
    }
    this.$log.warn(this.$filter.transform('ticket_DisassociateReporter_TicketDoesNotExist_Message'));
    return Promise.reject({ error_description: this.$filter.transform('ticket_DoesNotExist_Message') });
  };

  /** Handler for the on-assignee-changed event - changes the given assignee of the ticket */
  changeAssignee = (assigneeId: string): Promise<Ticket> => {
    if (this.ticket) {
      if (assigneeId) {
        this.watcherService.AddWatcher(this.ticket.ticketId, assigneeId).subscribe(data => {
          this.refresh();
        });
      }
      return this.updateProperty('assigneeId', assigneeId, UpdatePropertyAction.SetAfter);
    }
    this.$log.warn(this.$filter.transform('ticket_ChangeAssignee_TicketDoesNotExist_Message'));
    return Promise.reject({ error_description: this.$filter.transform('ticket_DoesNotExist_Message') });
  };

  /**
   * Determines if the ticket is in a state where the confirm-discard
   * prompt should be shown when the user attempts to navigate away from the page.
   */
  shouldShowConfirmDiscard = (): boolean => this.shouldShowConfirmDiscardInternal(true);
  /**
   * includeDraftTicketSubmissionConfirmation: flag to determine if we want to raise a confirmation dialog when
   * trying to navigate away from Draft ticket without submitting it.
   */
  private shouldShowConfirmDiscardInternal = (includeDraftTicketSubmissionConfirmation: boolean): boolean => {
    // if confirmation suppressed, return false
    if (this._suppressDiscardConfirmation) {
      return false;
    }
    // if the ticket is already deleted, return false
    const deleted = this.shouldDeleteTicket() || this._isTicketDeleted;

    const draftDirty =
      includeDraftTicketSubmissionConfirmation &&
      !deleted &&
      !this._isLoading &&
      this.ticketContextService.isDraftTicket() &&
      this.ticketContextService.canEditTicket() &&
      this.ticket.updateDate &&
      !isEqual(this.ticket.updateDate, this.ticket.creationDate);

    return draftDirty || this.deviceStateRegistry.isDiscardBlocked();
  };

  /** Sends the user back to the landing page */
  goToDashboard() {
    this.dashboardService.navigateToDashboard();
  }

  /**
   * Returns an object describing the maximum field lengths for a ticket.
   * These values correspond to TicketModel on the server.
   */
  getMaxLength = () => ({ title: 100, description: 4000, tag: 50 });

  /**
   * Returns true if any of the supplied arguments is truthy (expects objects that represent an error).
   * This will only be evaluated if the ticket has been initialized.
   *
   * @param args Variable list of arguments to check.
   * @return True if any of the supplied arguments are truthy.
   */
  hasError = (...args: any[]): boolean => {
    let error = false;
    if (this.ticket) {
      for (const arg of args) {
        error = error || arg;
      }
    }
    return error;
  };

  /** Sets the error */
  setError = (err: IError): void => {
    this.errorHandlerService.setError(err);
  };

  /** Clears the error */
  clearError = (): void => {
    this.errorHandlerService.clearError();
  };

  /** Returns true if the ticket has any activities */
  hasActivities = (): boolean =>
    this.ticketContextService.activities && this.ticketContextService.activities.length > 0;

  /** Adds an activity of the given type to the ticket */
  addActivity(activityTypeId: number, clearErrors: boolean = true, details: hd.IDictionary<any> = null) {
    const create = (): Activity => {
      const newActivity = { activityTypeId, details: {} } as Activity;

      if (!this.ticketContextService.activities) {
        this.ticketContextService.activities = [];
      }

      // Copy additional default parameters that were specified before adding to activity list
      if (details) {
        // for attachments, because there is number of old properties moved to details
        // for the enw model, to make cross model generation mapping keep these properties in
        // both places, until models are cleaned up
        if (isAttachmentActivity(newActivity)) {
          for (const key of Object.keys(details)) {
            newActivity.details[key] = newActivity[key] = details[key];
          }
        } else {
          for (const key of Object.keys(details)) {
            newActivity.details[key] = details[key];
          }
        }
      }

      if (isNoteActivity(newActivity) && newActivity?.details?.isResolution) {
        this.ticketContextService.activities.unshift((<unknown>newActivity) as Activity);
      } else if (!isDeviceSnapshotActivity(newActivity)) {
        this.ticketContextService.activities.push((<unknown>newActivity) as Activity);
      }
      return newActivity;
    };
    return Promise.resolve(create());
  }

  onWatchersListLoaded = (watchersList: WatcherDetails[]) => {
    this.watchersList = watchersList;
  };

  private scrollToDraftEmail = () => {
    // this.$scope.$broadcast('scrollToDraftEmail', this._currentEmailActivityIndex);
  };

  /**
   * Adds a new outbound email activity to the ticket activity. if no draft exist
   * Add reporter to the TO recipients list
   * Add watchers (Except current user and reporter) to the CC recipients list)
   */
  addOutboundEmailActivityRecipients = (popup: SotiPopup) => {
    if (this.isEmailDisabled()) {
      return;
    }
    popup.close();

    if (this.watchersList != null && this.watchersList.length > 0) {
      this.recipientsUserInfos = [];
      const currentUserId = this.authorizationService.getUserId();
      for (const watcher of this.watchersList) {
        if (watcher.userId !== currentUserId) {
          if (watcher.email) {
            if (
              // Todo: Remove forced type
              !this.isEmailAlreadyAdded(this.recipientsUserInfos, watcher.email)
            ) {
              this.recipientsUserInfos.push({
                name: watcher.displayName,
                address: watcher.email,
                userId: watcher.userId,
                userDetail: watcher,
                recipientType: watcher.role === 'Reporter' ? 'to' : 'cc',
                isInvalidAddress: false,
              });
            }
          }
        }
      }
    }

    this.addOutboundEmailActivity(this.recipientsUserInfos);
  };

  /**
   * Adds a new outbound email activity to the ticket activity.
   *
   * @param recipients Optional recipient list to initialize the outbound email with.
   */
  addOutboundEmailActivity = (recipients?: EmailRecipient[]) => {
    // Todo: Remove forced types
    if (this.isEmailDisabled()) {
      return;
    }

    const unsentEmailActivities = this.ticketContextService.activities.filter(activity =>
      this.isNewOrDraftEmail((<unknown>activity) as Activity)
    );

    const addNewActivity = unsentEmailActivities.length === 0;

    if (!addNewActivity && recipients) {
      // There's already an existing outbound email activity, so append the new recipient to its list
      const emailActivity = (<unknown>unsentEmailActivities[0]) as EmailActivity;

      const recipientList = (<unknown>this.ticketContextService.outboundEmailRecipientList) as EmailRecipient[];
      for (const recipient of recipients) {
        if (
          !this.isEmailAlreadyAdded(
            recipientList,
            isEmailRecipientWithUser(recipient) ? recipient.userDetail.email : recipient.address
          )
        ) {
          recipientList.push(recipient);
        }
      }

      this.focusOnActivity(emailActivity);
      this._currentEmailActivityIndex = this.ticketContextService.activities.indexOf(
        (<unknown>emailActivity) as Activity
      );
      // Remove? This does not make sense
      // this.ticketContextService.activities[this._currentEmailActivityIndex] = <unknown>emailActivity as IActivityModel;
      this.scrollToDraftEmail();
    } else if (addNewActivity) {
      this.addActivity(ActivityTypeEnum.outboundEmail).then((emailActivity: EmailActivity) => {
        if (recipients) {
          this.ticketContextService.outboundEmailRecipientList = recipients as any;
        } else {
          this.ticketContextService.outboundEmailRecipientList = [];
        }
        this.focusOnActivity(emailActivity);
        this._currentEmailActivityIndex = this.ticketContextService.activities.indexOf(
          (<unknown>emailActivity) as Activity
        );
      });
    }
  };

  /** Returns true if the currently logged in user has sufficient permissions
   * to send outbound email for the current ticket
   */
  canSendEmail = (): boolean => this.ticketContextService && this.ticketContextService.canSendEmail();

  /** Returns true if the email notification was disabled or  the currently
   * logged in user does not have permissions to send outbound email for the current ticket
   */
  isEmailDisabled = (): boolean => {
    let isDisabled = false;

    // Checking if the user has a permission to send email
    isDisabled = !this.ticketContextService.canSendEmail();

    // Checking if Outbound emails are configured
    isDisabled = isDisabled || this._emailConfig == null || !this._emailConfig.isEnabled;

    return isDisabled;
  };

  /** Add a new devicecapture Activity */
  addDeviceCaptureActivity = (file: File, device: IDeviceModel) => {
    // Time (in ms) to delay before hiding the progress indicator after an upload completes
    const uploadCompleteDelay = 500;

    this.attachmentService.uploadAttachment(file, 'attachment').subscribe(resultData => {
      const result = <IAttachmentModel>resultData;
      ///this.activity.fileId = result.attachmentId;

      // Upload complete: set progress to 100% and add a small delay before clearing
      // the client-side 'file' object. This delay allows time for the final progress
      // animation to complete and provides adequate visual feedback for very quick uploads.
      setTimeout(() => {
        ///this.file = undefined;
        // Need to manually trigger a digest cycle to update bindings
        // this.$scope.$apply(() => {
        //     // Fire upload complete callback (if it is set)
        //     if (uploadCompleteCallback) {
        //         uploadCompleteCallback(UploadCompleteReason.Success);
        //     }
        // });
      }, uploadCompleteDelay);
      const activityTemplate = {
        deviceId: device.deviceId,
        deviceName: device.deviceName,
        deviceFamily: device.family,
        mcConnectionId: device.mcConnectionId,
        status: DeviceCaptureActivityStatus.initialized,
        captureType: DeviceCaptureActivityTypes.image,
        files: [
          {
            fileId: result.attachmentId,
            fileName: result.fileName,
            fileSize: result.fileSize,
          },
        ],
      };

      this.addActivity(ActivityTypeEnum.deviceScreenshot, true, activityTemplate).then(activity => {
        // : hd.IDeviceCaptureActivity
        ///console.error('Adding Device Capture activity - This needs to be implemented for netCore', activity);
        // TODO: Figure out how attaching to and saving an activity works now
        //       The file attachments that are not pure activities now don't need activity IDs
        // TODO: Remove forced types once types are tidied up
        const temp = Object.assign(activity, {
          activityType: 'devicecapture',
          captureType: DeviceCaptureActivityTypes.image,
          deviceId: device.deviceId,
          deviceName: device.deviceName,
          mcConnectionName: this.rcDeviceInstanceName,
          fileSize: result.fileSize,
          fileData: JSON.stringify({
            fileId: result.attachmentId,
            fileName: result.fileName,
            fileSize: result.fileSize,
            displayName: device.deviceName,
          }),
        });
        this.ticketService
          .saveActivity((<unknown>temp) as IActivityModel, this.ticketContextService.ticket.ticketId)
          .subscribe(
            newActivity => {
              Object.assign(activity, newActivity);
              activity.details.fileData = undefined;

              this.ticketService
                .getActivity(parseInt(this.ticketContextService.ticket.ticketId, 10), activity.activityId)
                .pipe(
                  map((a: any) => {
                    // TODO - model fixes
                    const da = a as DeviceCaptureActivity;
                    this.patchActivity(da);
                    da.details.deviceId = da.details.deviceId || activity.details.deviceId;
                    da.details.deviceName = da.details.deviceName || activity.details.deviceName;
                    da.details.mcConnectionId = da.details.mcConnectionId || activity.details.mcConnectionId;
                    da.details.mcConnectionName = da.details.mcConnectionName || activity.details.mcConnectionName;
                    da.details.captureType = DeviceCaptureActivityTypes.image;
                    da.details.status = da.details.status || activity.details.status;
                    da.details.captureType = da.details.captureType || activity.details.captureType;
                    da.details.deviceFamily = da.details.deviceFamily || activity.details.deviceFamily;
                    da.details.captureDescription =
                      da.details.captureDescription || activity.details.captureDescription;
                    da.details.files = activityTemplate.files;
                    return da;
                  })
                )
                .subscribe(
                  da => Object.assign(activity, da),
                  err => console.error(err)
                );
            },
            error => {
              ////this.error = this.xsI18nPipe.transform('note_Save_Error');
              console.error(error);
            }
          );
      });
    });
  };

  /** Adds a new attachment for the specified file object */
  addAttachment = (file: File, invalidFiles: File[], associatedDeviceName?: string) => {
    if (!file) {
      // this.$rootScope.$emit('clearErrorSessions');
      if (invalidFiles && invalidFiles.length === 1) {
        const maxFileSizeBytes = this.getMaxAttachmentSize() * 1024 * 1024;
        const message = this.$filter.transform(
          'attachment_FileSizeExceeded_Error',
          invalidFiles[0].name,
          this.fileSizeFormatter.transform(maxFileSizeBytes)
        );
        window.alert(message);
      }
      return;
    }

    console.log(file);
    // 1. create the activity but do not save it yet
    const defaultParams: hd.IDictionary<any> = {
      fileName: file.name,
      fileSize: file.size,
      description: '',
      $$file: file,
      $$isLocal: true, // addActivity currently only creates new activities
    };
    if (associatedDeviceName) {
      defaultParams.displayName = associatedDeviceName;
    }
    this.addActivity(ActivityTypeEnum.attachment, true, defaultParams).then(newActivity => {
      console.log(newActivity);
      ((<unknown>newActivity) as IAttachmentActivity).$$isLocal = newActivity.activityId === undefined;
    });
  };

  /** Returns max supported attachment size in megabytes */
  getMaxAttachmentSize = (): number => Math.min(this._maxAttachmentSize, this._maxApiFileSize);

  attachScreenshot = (result: any) => {
    // unlike other DeviceCaptureActivity attachments, screenshot is created in the browser
    // and the file is being uploaded from the browser. Check that the file is smaller
    // than the maximum upload size allowed
    if (result !== undefined && result.attachment) {
      const maxFileSizeBytes = this.getMaxAttachmentSize() * 1024 * 1024;
      if (result.fileObject.size <= maxFileSizeBytes) {
        this.addDeviceCaptureActivity(result.fileObject, result.device);
      } else {
        const message = this.$filter.transform(
          'attachment_FileSizeExceeded_Error',
          result.fileObject.name,
          this.fileSizeFormatter.transform(maxFileSizeBytes)
        );
        window.alert(message);
      }
    }
  };

  /** Check if ticket should be in disabled state */
  isTicketLoading = (): boolean => this.isProcessing() || this.isLoading();

  isReporterMethodDisabled = (): boolean => !this.isNewTicket() || !this.ticketContextService.canEditTicket();

  canViewPriority = (): boolean => this.ticketContextService && this.ticketContextService.canViewPriority();

  isPriorityDisabled = (): boolean => !this.ticketContextService.canEditPriority() || this.ticket.status === 'resolved';

  isAddDeviceDisabled = (): boolean => !this.ticketContextService.canAddDevice() || this.ticket.status === 'resolved';

  isStatusDisabled = (): boolean => this.isProcessing('status') || !this.ticketContextService.canEditStatus();

  setDescriptionEditing = (value: boolean) => {
    this._isEditingDescription = value;
    this.updateDescriptionState();
  };

  isDescriptionChanged = (): boolean => this.description !== this.ticket.description;

  resetDescription = () => {
    this.description = this.ticket.description;
    this.updateDescriptionState();
  };

  titleIsHighlighted = () =>
    this.ticketContextService.isDraftTicket() && (!this.ticket.title || this.ticket.title.length === 0);

  confirmAction = () => {
    if (this.shouldDeleteTicket() && !this.isLoading() && this.ticketContextService.canEditTicket()) {
      this.deleteTicket();
      this.stopCurrentRemoteControlActivity();
    }
  };

  /** Returns true if the user defined tag is enabled */
  isUserDefinedTagEnabled = (): boolean => {
    if (!!this._tagConfig) {
      return this._tagConfig.isUserDefinedTagEnabled;
    }
    return false;
  };

  canCreateUserDefinedTag = (): boolean => {
    const retValue =
      this.isUserDefinedTagEnabled() && this.ticketContextService && this.ticketContextService.canAssociateTag();
    return retValue;
  };

  /** Returns true if user should provide note resolving the ticket */
  isNoteRequiredOnResolve = (): boolean => this._ticketConfiguration.isNoteRequiredOnResolve;

  isAnyNoteActivityDirty = (): boolean => {
    if (this.hasActivities()) {
      for (const activity of this.ticketContextService.activities as any) {
        const newActivity: NoteActivity = activity as NoteActivity;
        if (isNoteActivity(newActivity) && newActivity.description !== '') {
          return true;
        }
      }
    }

    return false;
  };

  getMainDateItem() {
    if (
      this.ticket.resolutionTargetDate &&
      this.ticket.status !== 'resolved' &&
      this.ticketContextService.canViewSLA()
    ) {
      return this._dueDate;
    }
    return this.isNewTicket() ? this._createdDate : this._updateDate;
  }

  getOtherDateItems() {
    if (this.isOpenOrPending || (this.ticket.resolutionTargetDate && this.ticket.status !== 'resolved')) {
      return [this._createdDate, this._updateDate];
    }
    return [this._createdDate];
  }

  isRcStarted = (): boolean => false;
  // if (!this.isRemoteControlStarted && this._rcPanelState) {
  //     const rcState = this.remoteControlService.getCurrentState();
  //     this.isRemoteControlStarted = rcState ? rcState.firstScreenLoaded : false;
  // }
  // return this.isRemoteControlStarted;

  restoreRcPanel() {
    // XTODO: fire events
    this.openAssoicationPanel();
    this.workPanelCollapse(true);
    this.isSplashScreen = true;
  }

  shouldScrolltoLastActivity = (): boolean => false;
  // return this.ticketService.getCurrentTicketStatus() !== 'resolved';

  getStatusCaption = (status: string): string => {
    let caption = '';

    switch (status) {
      case 'draft':
        caption = 'ticket_Draft_Caption';
        break;
      case 'open':
        caption = this.isNewTicket() ? 'ticket_SubmitAsOpen_Caption' : 'ticket_Open_Caption';
        break;
      case 'pending':
        caption = this.isNewTicket() ? 'ticket_SubmitAsPending_Caption' : 'ticket_Pending_Caption';
        break;
      case 'resolved':
        caption = this.isNewTicket() ? 'ticket_SubmitAsResolved_Caption' : 'ticket_Resolved_Caption';
        break;
      default:
        return status;
    }

    return this.$filter.transform(caption);
  };

  getStatus(): string {
    return this.ticket?.status;
  }

  currentStatusIs = (status: string): boolean => {
    let result = false;
    if (this.ticket && this.ticket.status === status) {
      result = true;
    }

    return result;
  };

  private isEmailAlreadyAdded = (recipientEmailList: EmailRecipient[], recipient: string): boolean => {
    for (const emailRecipient of recipientEmailList) {
      if (emailRecipient.address.toUpperCase() === recipient.toUpperCase()) {
        return true;
      }
    }
    return false;
  };

  private isNewOrDraftEmail = (activity: Activity): boolean => {
    if (!isOutboundEmailActivity(activity)) {
      return false;
    }
    return !activity.activityId || activity.status === 'draft';
  };

  private shouldDeleteTicket = (): boolean => {
    const assigneeTest = !this.ticket.assigneeId;

    const reporterTest = !this.ticket.reporterId;

    return (
      this.ticketContextService.isDraftTicket() &&
      !this._isTicketDeleted &&
      !this._isTicketUpdated &&
      this.ticket.updateDate === this.ticket.creationDate &&
      assigneeTest &&
      (this.ticket.description == null || this.ticket.description === '') &&
      reporterTest &&
      (this.ticket.title == null || this.ticket.title === '') &&
      this.ticketContextService.activities.filter(
        activity => activity.activityId || !this.isResolutionNote((<unknown>activity) as Activity)
      ).length <= 0
    );
  };

  private updateDescriptionState = () => {
    let state: string;

    if (this.isDescriptionChanged() || this._isEditingDescription) {
      state = 'active';
    } else if (this.ticketContextService.isDraftTicket() && (!this.description || this.description.length === 0)) {
      state = 'highlight';
    } else {
      state = 'inactive';
    }

    this.descriptionState = state;
  };

  /** Asks the user if he wants to close the previous RC session and if so, starts the new one
   * Returns true if the new session was confirmed, false if the user decided not to close the
   * first session
   */
  private confirmAndStartRemoteControl = (device: IDeviceModel, startPoppedOut: boolean = false): void => {
    const handleStart = () => {
      if (!this.remoteControlService.confirmStartRC()) {
        return;
      }

      if (this.workPanelCollapsed) {
        this.workPanelCollapse(false);
      }

      // If there is a previous RC session now is the moment to close it.
      // If the new RC session fails, we need to have already closed the former
      const currentRcState = this.deviceStateRegistry.getCurrentState();
      if (currentRcState && currentRcState.device && currentRcState.device.deviceId !== device.deviceId) {
        // Close the current RC session
        if (this.remoteControlService.stopRemoteControl(currentRcState.device)) {
          this.globalEventBusService.broadcast(GlobalEvents.remoteControlCloseInitiated, null);
        }
      }
      const defaultParams: hd.IDictionary<any> = {
        deviceId: device.deviceId,
        creationUserId: this.authorizationService.getUserId(),
        $$ticketId: this.ticket.ticketId,
      };
      this.ticketContextService.activities = this.ticketContextService.activities.filter(
        activity => !isRemoteControlActivity((<unknown>activity) as Activity)
      );
      this.addActivity(ActivityTypeEnum.remoteControl, false, defaultParams).then((activity: RemoteControlActivity) => {
        this.rcServiceCalls(device, startPoppedOut);
      });
      if (!this.workPanelManagerService.isPanelTypePoppedOut(WorkPanelType.rc) && !startPoppedOut) {
        this.globalEventBusService.broadcast(GlobalEvents.openRcWorkPanel, {
          device,
          workPanelKey: null,
        } as IWorkPanelInformation);
        this.closeAssoicationPanel();
      }
    };

    setTimeout(() => {
      handleStart();
    });
  };

  private rcServiceCalls(device: IDeviceModel, startPoppedOut: boolean) {
    this.removeOldCallbacks(device);
    // If we are starting an RC session that will be rendered on a popout, then we dont need
    // this callbacks as they modify main window panels
    // if (!startPoppedOut) {
    //   this.remoteControlService.setRemoteControlStartedCallback(this.rcOrSnapshotStarted, device.deviceId);
    // }
    this.remoteControlService.startRemoteControl(device, this.ticket.ticketId.toString(), startPoppedOut).then(() => {
      this.isRemoteControlStarted = true;
      this.getDeviceInstanceName(device).subscribe(name => {
        this.rcDeviceInstanceName = name;
      });
    });
  }

  private removeOldCallbacks(device: IDeviceModel) {
    this.deviceStateRegistry.removeRemoteControlStartedCallback(this.rcOrSnapshotStarted, device.deviceId);
    if (this.popoutRemoteControlStopped) {
      this.popoutRemoteControlStopped.close();
    }
  }

  private rcOrSnapshotStarted = () => {
    this.closeAssoicationPanel();
    // this.$timeout(() => {
    //     this.$rootScope.$digest();
    // });
  };

  public openRcPanel(panel?: string) {
    if (this.isRCMinimized) {
      this._rcPanelState = true;
      this.workPanelCollapse(false);
    } else {
      // this.$timeout(() => {
      //     this.$scope.$digest();
      // });
    }
    this.isSplashScreen = false;
  }

  /**
   * Initialize the controller for an existing ticket .
   * Setup the ticket, then the device details, then the activity details
   */
  private initExistingTicket(ticket: Ticket) {
    // Set the page title with the ticket ID and Title
    let ticketTitle = ticket.title;
    if (!ticketTitle && this.isNewTicket()) {
      const ticketStatus = this.getTicketStatus(ticket.status) as IDropdownListItem;
      if (ticketStatus) {
        ticketTitle = ticketStatus.caption;
      }
    }
    this.pageTitleService.setTitle(this.$filter.transform('viewTicket_PageTitle', ticket.ticketId, ticketTitle));

    // Set the ticket description
    this.description = ticket.description;
    this.updateDescriptionState();

    const nestedPromises: Promise<any>[] = [];

    // Get the ticket activities
    // XTODO: reimplement with observables
    this.refreshActivities(ticket);

    // nestedPromises.push(this.ticketContextService.activities.$promise);

    // Once all promises have been resolved or rejected, set the ticket to loaded
    // this.$q.allSettled(nestedPromises).finally(() => this.setLoaded());
  }

  private compareTicketProperties(propertyName: string, value: any): boolean {
    if ((propertyName === 'assigneeId' || propertyName === 'reporterId') && this.ticket[propertyName] && value) {
      return this.ticket[propertyName].toLowerCase() === value.toLowerCase();
    }
    return this.ticket[propertyName] === value;
  }

  public TicketLoaded: EventEmitter<Ticket> = new EventEmitter<Ticket>();

  /** Marks the ticket as loaded */
  private setLoaded = (): void => {
    this._isLoading = false;
    this.TicketLoaded.next(this.ticket);
    this.globalEventBusService.broadcast(GlobalEvents.ticketLoaded, null);
  };

  /**
   * Sets whether or not the ticket or a specified field on the ticket is being updated.
   *
   * @param setValue Whether the ticket or ticket property is processing.
   * @param propertyName Optional ticket property name to update the processing state for.
   */
  private setProcessing = (setValue: boolean, propertyName?: string): void => {
    if (propertyName) {
      this._processingProperties[propertyName] = setValue;
    } else {
      this._isProcessing = setValue;
    }
  };

  public checkForPendingTransfer = (): boolean => {
    const deviceId = this.remoteControlService.endRemoteControlTransfer();

    if (deviceId) {
      if (!this.ticket) {
        return false;
      }

      const defaultParams: hd.IDictionary<any> = {
        creationDate: this.remoteControlService.getRcStartTime(deviceId).toISOString(),
        deviceId,
        creationUserId: this.authorizationService.getUserId(),
        $$ticketId: this.ticket.ticketId,
      };

      // There was a pending RC session transfer for deviceId, so add a local RC session activity
      this.addActivity(ActivityTypeEnum.remoteControl, true, defaultParams).then((activity: Activity) => {
        this.rcOrSnapshotStarted();
        this.remoteControlService.updateTicketId(deviceId, this.ticket.ticketId);
        this.globalEventBusService.broadcast(GlobalEvents.remoteControlCompleteTransfer, true);
      });

      if (this.ticket.devices === null || this.ticket.devices.find(dev => dev.deviceId === deviceId) === undefined) {
        // Device is not associated with the ticket yet, so attempt to associate it
        const newAssociation = { ticketId: this.ticket.ticketId.toString(), deviceId } as hd.IAssociatedDeviceModel;
        newAssociation.deviceAssociationId = newAssociation.deviceId;
        this.ticketService
          .createAssociatedDevice(newAssociation)
          .pipe(first())
          .subscribe(
            () => {
              // const index = this.ticketContextService.associatedDevices.push(newAssociation) - 1;
              this.refresh();
            },
            (error: any) => {
              // Error saving: remove the association from the list
              console.log('could not associate device ' + error);
            }
          );
      } else {
        console.log('device already associated with ticket');
      }
    }
    return true;
  };

  /**
   *  Stop the current remote control section
   */
  public stopCurrentRemoteControlActivity = (): void => {
    if (this.hasActivities()) {
      for (const activities of this.ticketContextService.activities as any) {
        const newActivity: RemoteControlActivity = activities as RemoteControlActivity;
        if (isRemoteControlActivity(newActivity)) {
          this.ticketContextService.activities = this.ticketContextService.activities.filter(
            activity => !isRemoteControlActivity((<any>activity) as Activity)
          );
        }
      }
    }
  };

  /**
   * Called when the rc popout window is either opened or closed.
   *
   * @param poppedOut True if the popout window was opened, false if it was closed.
   */
  private onRemoteControlPopout = (poppedOut: boolean): void => {
    this.isRcPoppedOut = poppedOut;
    // When RC session is popped out, restore the association panel - otherwise minimize (on popin)
    if (poppedOut) {
      if (!this.workPanelCollapsed) {
        this.workPanelCollapse(true);
        this.sidePanelCollapse(false);
      }
      // Raise event directly as panel is already expanded
      this.globalEventBusService.broadcast(GlobalEvents.remoteControlOpenPopoutWindow, null);

      if (
        !this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.filePreview) &&
        !this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.savedSnapshot)
      ) {
        this.restoreRcPanel();
      }
    } else {
      this.closeAssoicationPanel();
      if (!this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.filePreview)) {
        this.openRcPanel();
      }
    }
  };

  /** Validate the Pre-Defined Tags requirements and trigger show warning message if required */
  private validateAndShowPredefinedTagMessage = (): boolean => {
    const associatedTags = this.ticket.tags || [];
    if (
      this._tagConfig.isPreDefinedTagRequired &&
      (associatedTags === null || associatedTags.length === 0 || !this.preDefinedTagExists(associatedTags))
    ) {
      this._isPreDefinedTagRequiredWindowToggleOpen = true;
    } else {
      this._isPreDefinedTagRequiredWindowToggleOpen = false;
    }
    return (
      this._tagConfig.isPreDefinedTagRequired &&
      (associatedTags === null || associatedTags.length === 0 || !this.preDefinedTagExists(associatedTags))
    );
  };

  // validates if the tag list contains any predefined tag
  private preDefinedTagExists = (associatedTags): boolean => {
    let tagExists = false;
    associatedTags.forEach(tag => {
      if (tag.tagGroupId) {
        tagExists = true;
      }
    });
    return tagExists;
  };

  private onDeviceSnapshotStatusChanged = (status: IDeviceSnapshotStatus): void => {
    console.log('TicketPageService -> onDeviceSnapshotStatusChanged', status);
    if (status.status === DeviceSnapshotSaveRequestStatus.SaveRequested) {
      const snapshot = status.snapshot as SnapshotModel;
      this.snapshotService.saveDeviceSnapshot(snapshot, this.ticket.ticketId).subscribe(
        activityId => {
          if (activityId !== -1) {
            this.ticketService.getActivity(this.ticket.ticketId, activityId).subscribe(activity => {
              this.patchActivity((<unknown>activity) as Activity);
              console.log('added Activity', activity);
              this.ticketContextService.activities.push((<unknown>activity) as Activity);
            });
            // Let the rest of the system know that we are done with the snapshot saving
            this.deviceSnapshotStatusChanged.postMessage({
              action: status.action,
              status: DeviceSnapshotSaveRequestStatus.None,
              activityId,
              deviceId: status.deviceId,
            } as IDeviceSnapshotStatus);
          }
        },
        (result: any) => {
          // Handle errors...
          this.deviceSnapshotStatusChanged.postMessage({
            action: status.action,
            status: DeviceSnapshotSaveRequestStatus.SaveErrored,
            activityId: null,
            deviceId: status.deviceId,
            message: result.error,
          } as IDeviceSnapshotStatus);
        }
      );
    }
  };

  private onPopoutRemoteControlStart = (device: IDeviceModel): void => {
    this.startRemoteControl(device, true);
  };

  private closeAssoicationPanel = () => {
    // XTODO: fire events
    this.sidePanelCollapse(true);
    // if (!this.isPanelMinimized('associationBar')) {
    //     this.minimizeSidePanel('associationBar', 'associationBarMinimized', true);
    // }
  };

  private openAssoicationPanel = () => {
    // XTODO: fire events
    this.sidePanelCollapse(false);
  };

  /*
    This method is a workaround for a known bug in Firefox:
    According to https://bugzilla.mozilla.org/show_bug.cgi?id=652543
    and https://stackoverflow.com/q/16222805
    Firefox isn't consistently firing 'transitionend' event in case we apply multiple transitions at a time.
    By using this method we apply them one by one.
    */
  private removeClassDelayed(element: angular.IAugmentedJQuery, className: string) {
    setTimeout(() => {
      element.removeClass(className);
    }, 0);
  }

  private get isOpenOrPending(): boolean {
    return (
      this.featureToggleService.isEnabled('AdvancedSla') &&
      (this.ticket.status === 'open' || this.ticket.status === 'pending')
    );
  }

  private configureSlaTimer = () => {
    this.targetResolutionDate = this.ticket.resolutionTargetDate;
    if (this.ticket.status === 'pending') {
      this.ticketService.getTicketWork(this.ticket.ticketId.toString()).subscribe(
        (response: ITicketWorkInfoModel) => {
          this.elapsedTime = response.elapsedTime;
          this.serviceTarget = response.serviceTarget;
          this.status = this.ticket.status;
        },
        error => {
          this.$log.debug('Failed to load sla work details');
          this.$log.error(error);
          this.setError(error);
        }
      );
    } else {
      this.status = this.ticket.status;
    }
  };

  /**
   * Patches the raw activity recieved from the backend to match our model
   *
   * @param activity
   * @returns activity
   * Note: This mutates the input
   */
  private patchActivity(activity: Activity) {
    if (typeof activity.details === 'object' && Object.keys(activity.details).includes('0')) {
      activity.details = activity.details[0];
    }
    if (isDeviceSnapshotActivity(activity) && typeof activity.details.data === 'string') {
      // Snapshot
      activity.details.data = JSON.parse(activity.details.data);
    } else if (isDeviceCaptureActivity(activity)) {
      if (!activity.details.captureType) {
        // TODO remove this cleanup, captureType is now assigned in the DB
        console.error(`Activity captureType for activity ${activity.activityId} should not be empty or null`, activity);
        if (activity.activityTypeId === ActivityTypeEnum.deviceVideo) {
          activity.details.captureType = DeviceCaptureActivityTypes.video;
        } else if (activity.activityTypeId === ActivityTypeEnum.deviceLog) {
          activity.details.captureType = DeviceCaptureActivityTypes.adbLogs;
        } else if (activity.activityTypeId === ActivityTypeEnum.deviceScreenshot) {
          activity.details.captureType = DeviceCaptureActivityTypes.image;
        }
      }
    }
    return activity;
  }

  /** Find the mcInstance Name for device */
  getDeviceInstanceName = (device: IDeviceModel): Observable<string> => {
    if (device.mcConnectionId) {
      return this.mcConnectionService
        .getById(device.mcConnectionId)
        .pipe(map((value: IMCConnection) => value.connectionName));
    }
    return this.mcConnectionService.getSotiAssistMcName().pipe(map(connection => connection.mcConnectionName));
  };

  getTicketConfig = (): Observable<ITicketConfiguration> => this.ticketConfigSubject.asObservable();

  getAttachmentConfig = (): Observable<IAttachmentServiceConfig> => this.attachmentConfigSubject.asObservable();
}
